import { useState, useCallback, useEffect } from 'react';
import type { OptimisticMessage } from '../contexts/CommunityContext';
import { isMessageSelectable } from '../utils/messageSelectionUtils';

export function useMessageSelection(clearWhen?: string | null) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const isSelectionMode = selectedIds.size > 0;

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const toggleMessage = useCallback((message: OptimisticMessage) => {
    if (!isMessageSelectable(message)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(message.community_message_id)) {
        next.delete(message.community_message_id);
      } else {
        next.add(message.community_message_id);
      }
      return next;
    });
  }, []);

  const isSelected = useCallback(
    (messageId: string) => selectedIds.has(messageId),
    [selectedIds]
  );

  useEffect(() => {
    clearSelection();
  }, [clearWhen, clearSelection]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedIds.size > 0) {
        clearSelection();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedIds.size, clearSelection]);

  return {
    selectedIds,
    isSelectionMode,
    count: selectedIds.size,
    clearSelection,
    toggleMessage,
    isSelected,
  };
}
